import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { TvAlert, TvButton, TvDiv, TvH4, TvSmall } from "@trustvid/design-system";
import { buildScopedPath } from "../../../utils/account-context";
import { clearUserSession, persistUserData } from "../../../utils/auth-helpers";
import { buildPostSignupPath } from "../../../utils/saasPostSignupPath";
import { decodeGoogleSsoSessionParam } from "../../../utils/googleSso";
import AuthSplitLayout from "./AuthSplitLayout";
import { AUTH_FEATURE_PANEL_HEADING } from "./loginPageContent";

const EXISTING_ACCOUNT_CODES = new Set(["GOOGLE_ACCOUNT_EXISTS", "EMAIL_IN_USE"]);
const MISSING_ACCOUNT_CODES = new Set(["GOOGLE_USER_NOT_FOUND"]);

/**
 * Landing page after Google OAuth (API redirects here with token + session).
 */
const GoogleAuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("Completing Google sign-in...");
  const [errorCode, setErrorCode] = useState("");

  useEffect(() => {
    const error = searchParams.get("error");
    const code = searchParams.get("code") || searchParams.get("errorCode") || "";
    if (error) {
      clearUserSession();
      setStatus("error");
      setErrorCode(code);
      if (EXISTING_ACCOUNT_CODES.has(code)) {
        setMessage(
          error ||
            "You already have an existing TrustVid account. Please log in with email/password or Google SSO.",
        );
      } else if (MISSING_ACCOUNT_CODES.has(code)) {
        setMessage(error || "No TrustVid account found for this Google user. Sign up first.");
      } else {
        setMessage(error);
      }
      return;
    }

    const token = searchParams.get("token");
    const accountId = searchParams.get("accountId");
    const isNew = searchParams.get("isNew") === "1";
    const mfaRequired = searchParams.get("mfaRequired") === "1";
    const user = decodeGoogleSsoSessionParam(searchParams.get("session"));

    if (!token || !accountId) {
      clearUserSession();
      setStatus("error");
      setMessage("Google sign-in did not return a session. Please try again.");
      return;
    }

    localStorage.setItem("token", token);
    localStorage.setItem("accountId", String(accountId));
    localStorage.setItem("userType", "user");
    if (user) {
      persistUserData(user);
    }

    if (mfaRequired) {
      setStatus("mfa");
      setMessage("MFA required. Continue on the login page to enter your code.");
      setTimeout(() => {
        navigate("/login", { replace: true, state: { googleMfaRequired: true } });
      }, 600);
      return;
    }

    setStatus("success");
    setMessage(isNew ? "Account created. Opening plans..." : "Signed in. Redirecting...");
    const nextPath = isNew
      ? buildPostSignupPath(accountId)
      : buildScopedPath(accountId, "/dashboard");
    // Hard navigation on session boundary so App-root providers (AccountBrandingProvider,
    // etc.) remount with the new tenant. See Login.js for the full rationale.
    setTimeout(() => window.location.assign(nextPath), 800);
  }, [navigate, searchParams]);

  const preferLogin = EXISTING_ACCOUNT_CODES.has(errorCode);
  const preferSignup = MISSING_ACCOUNT_CODES.has(errorCode);

  return (
    <AuthSplitLayout
      pageTestId="google-auth-callback-page"
      cardTestId="google-auth-callback-card"
      brandTestId="auth-platform-brand"
      showCardHeading={false}
      cardTitle="Google sign-in"
      cardSubtitle="Finishing secure sign-in with your Google account."
      featurePanelHeading={AUTH_FEATURE_PANEL_HEADING}
    >
      <TvDiv className="tv-login-page__form" data-testid="google-auth-callback-status">
        <TvH4 className="tv-login-page__card-heading fw-bold text-dark mb-2">Google sign-in</TvH4>
        <TvSmall className="text-muted d-block mb-3">
          We never store your Google password — only a verified TrustVid session.
        </TvSmall>

        {status === "loading" ? (
          <TvAlert color="info" fade={false}>
            {message}
          </TvAlert>
        ) : null}
        {status === "success" || status === "mfa" ? (
          <TvAlert color="success" fade={false}>
            {message}
          </TvAlert>
        ) : null}
        {status === "error" ? (
          <TvAlert color="danger" data-testid="google-auth-callback-error" fade={false}>
            {message}
          </TvAlert>
        ) : null}

        {status === "error" ? (
          <TvDiv className="mt-3 d-flex flex-column gap-2">
            {preferSignup ? (
              <>
                <Link to="/register">
                  <TvButton
                    color="primary"
                    className="tv-login-page__submit w-100"
                    data-testid="google-callback-cta-signup"
                  >
                    Create account
                  </TvButton>
                </Link>
                <Link to="/login">
                  <TvButton color="secondary" outline className="w-100" data-testid="google-callback-cta-login">
                    Back to sign in
                  </TvButton>
                </Link>
              </>
            ) : preferLogin ? (
              <>
                <Link to="/login">
                  <TvButton
                    color="primary"
                    className="tv-login-page__submit w-100"
                    data-testid="google-callback-cta-login"
                  >
                    Back to sign in
                  </TvButton>
                </Link>
                <Link to="/register">
                  <TvButton color="secondary" outline className="w-100" data-testid="google-callback-cta-signup">
                    Create account
                  </TvButton>
                </Link>
              </>
            ) : (
              <>
                <Link to="/login">
                  <TvButton color="primary" className="tv-login-page__submit w-100" data-testid="google-callback-cta-login">
                    Back to sign in
                  </TvButton>
                </Link>
                <Link to="/register">
                  <TvButton color="secondary" outline className="w-100" data-testid="google-callback-cta-signup">
                    Create account
                  </TvButton>
                </Link>
              </>
            )}
          </TvDiv>
        ) : null}
      </TvDiv>
    </AuthSplitLayout>
  );
};

export default GoogleAuthCallback;
